<script setup>
import { onMounted, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { debounce } from '@chatwoot/utils';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const DEBOUNCE_DELAY = 300;

const { t } = useI18n();
const store = useStore();

const dndContacts = useMapGetter('dnd/getRecords');
const dndUIFlags = useMapGetter('dnd/getUIFlags');

const searchQuery = ref('');
const disableModalRef = ref(null);
const contactToDisable = ref(null);

const isFetching = computed(() => dndUIFlags.value.isFetching);
const hasContacts = computed(() => dndContacts.value.length > 0);

const fetchContacts = async () => {
  console.log(
    '[DNDManagement] Fetching DND contacts with search:',
    searchQuery.value
  );
  try {
    const params = {};
    if (searchQuery.value) {
      params.search = searchQuery.value;
    }
    const result = await store.dispatch('dnd/fetchDNDContacts', params);
    console.log('[DNDManagement] Fetched contacts:', result);
  } catch (error) {
    console.error('[DNDManagement] Error fetching contacts:', error);
    useAlert(t('DND.MESSAGES.ERROR'));
  }
};

const debouncedSearch = debounce(fetchContacts, DEBOUNCE_DELAY);

const handleSearch = () => {
  debouncedSearch();
};

const openDisableModal = contact => {
  contactToDisable.value = contact;
  disableModalRef.value?.open();
};

const closeDisableModal = () => {
  contactToDisable.value = null;
  disableModalRef.value?.close();
};

const handleDisableDND = async () => {
  if (!contactToDisable.value) return;

  try {
    await store.dispatch('dnd/disableDND', [contactToDisable.value.id]);
    useAlert(t('DND.MESSAGES.DISABLED_SUCCESS'));
    await fetchContacts();
    disableModalRef.value?.close();
    contactToDisable.value = null;
  } catch (error) {
    useAlert(t('DND.MESSAGES.ERROR'));
  }
};

const formatDate = date => {
  if (!date) return '';
  try {
    // Convert ISO string to Date object if needed
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dynamicTime(dateObj);
  } catch (error) {
    console.error('[DNDManagement] Error formatting date:', error, date);
    return '';
  }
};

onMounted(() => {
  console.log('[DNDManagement] Component mounted');
  console.log('[DNDManagement] dndContacts:', dndContacts.value);
  console.log('[DNDManagement] dndUIFlags:', dndUIFlags.value);
  fetchContacts();
});
</script>

<template>
  <div class="flex flex-col w-full h-full">
    <!-- Header -->
    <div class="flex-shrink-0 px-8 py-6 border-b border-n-weak">
      <h1 class="text-2xl font-semibold text-n-slate-12 mb-2">
        {{ $t('DND.HEADER') }}
      </h1>
      <p class="text-sm text-n-slate-11">
        {{ $t('DND.DESCRIPTION') }}
      </p>
    </div>

    <!-- Search Bar -->
    <div class="flex-shrink-0 px-8 py-4 border-b border-n-weak">
      <div class="max-w-md">
        <input
          v-model="searchQuery"
          type="search"
          :placeholder="$t('DND.FILTERS.SEARCH_PLACEHOLDER')"
          class="w-full h-10 px-4 text-sm border border-n-weak rounded-lg bg-n-background text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-blue-8"
          @input="handleSearch"
        />
      </div>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-auto px-8 py-6 bg-n-background">
      <!-- Loading State -->
      <div v-if="isFetching" class="flex items-center justify-center h-96">
        <Spinner />
      </div>

      <!-- Empty State -->
      <div
        v-else-if="!hasContacts"
        class="flex flex-col items-center justify-center h-96"
      >
        <div class="text-center">
          <span class="i-lucide-bell-off size-16 text-n-slate-9 mb-4" />
          <h3 class="text-lg font-medium text-n-slate-12 mb-2">
            {{ $t('DND.TABLE.EMPTY_STATE.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11">
            {{ $t('DND.TABLE.EMPTY_STATE.MESSAGE') }}
          </p>
        </div>
      </div>

      <!-- DND Contacts Table -->
      <div v-else class="overflow-x-auto">
        <table class="min-w-full divide-y divide-n-weak">
          <thead class="bg-n-alpha-2">
            <tr>
              <th
                scope="col"
                class="px-6 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider"
              >
                {{ $t('DND.TABLE.COLUMNS.NAME') }}
              </th>
              <th
                scope="col"
                class="px-6 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider"
              >
                {{ $t('DND.TABLE.COLUMNS.EMAIL') }}
              </th>
              <th
                scope="col"
                class="px-6 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider"
              >
                {{ $t('DND.TABLE.COLUMNS.PHONE') }}
              </th>
              <th
                scope="col"
                class="px-6 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider"
              >
                {{ $t('DND.TABLE.COLUMNS.REASON') }}
              </th>
              <th
                scope="col"
                class="px-6 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider"
              >
                {{ $t('DND.TABLE.COLUMNS.ENABLED_DATE') }}
              </th>
              <th scope="col" class="relative px-6 py-3">
                <span class="sr-only">{{ $t('DND.ACTIONS.REMOVE_DND') }}</span>
              </th>
            </tr>
          </thead>
          <tbody class="bg-n-background divide-y divide-n-weak">
            <tr
              v-for="contact in dndContacts"
              :key="contact.id"
              class="hover:bg-n-alpha-1"
            >
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <div
                    class="flex-shrink-0 size-10 bg-n-blue-3 rounded-full flex items-center justify-center"
                  >
                    <span class="text-sm font-medium text-n-blue-11">
                      {{ contact.name?.[0]?.toUpperCase() || '?' }}
                    </span>
                  </div>
                  <div class="ml-4">
                    <div class="text-sm font-medium text-n-slate-12">
                      {{ contact.name || 'Unknown' }}
                    </div>
                  </div>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-n-slate-11">
                {{ contact.email || '-' }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-n-slate-11">
                {{ contact.phone_number || '-' }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-n-slate-11">
                {{ contact.dnd_reason || '-' }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-n-slate-11">
                {{ formatDate(contact.dnd_updated_at) }}
              </td>
              <td
                class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium"
              >
                <Button
                  variant="link"
                  color="ruby"
                  size="sm"
                  :label="$t('DND.ACTIONS.REMOVE_DND')"
                  @click="openDisableModal(contact)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Disable DND Modal -->
    <Dialog
      ref="disableModalRef"
      :title="$t('DND.MODAL.DISABLE.TITLE')"
      :description="$t('DND.MODAL.DISABLE.DESCRIPTION')"
      :show-cancel-button="false"
      :show-confirm-button="false"
      @close="closeDisableModal"
    >
      <template #footer>
        <div class="flex justify-end gap-2 w-full">
          <Button
            variant="outline"
            color="slate"
            :label="$t('DND.MODAL.DISABLE.CANCEL')"
            type="button"
            @click="closeDisableModal"
          />
          <Button
            color="ruby"
            :label="$t('DND.MODAL.DISABLE.CONFIRM')"
            :is-loading="dndUIFlags.isDisabling"
            type="button"
            @click="handleDisableDND"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>
